import { skipToken, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Workspaces } from '@air/api';
import { ONE_HOUR } from '@air/utilities';
import { callAirApi } from '@air/utils-api';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscription } from '~/swr-hooks/subscriptions/useSubscription';
import {
  type CreditStatsResponse,
  canHaveEnforcedCredits,
  isCreditUsageVisible,
  normalizeCreditStats,
} from '~/utils/creditUtils';

export const CREDIT_STATS = 'CREDIT_STATS';

export const getCreditStatsKey = (params: { workspaceId?: string }) => {
  return [CREDIT_STATS, params];
};

export const useCreditStats = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const subscription = useSubscription();
  const pricingModel = subscription.data?.pricingModel;

  const { creditUsageEnabledForLegacyPlans } = useTemporaryFeatureFlags();
  const isCreditUsageEnabled = creditUsageEnabledForLegacyPlans || canHaveEnforcedCredits(subscription.data);

  // Fixed plans are fetched before the backend enforces their credits; until it does, consumers see no stats.
  // Normalized here rather than in queryFn because other tabs write raw responses into the cache.
  const selectVisibleStats = useCallback(
    (response: CreditStatsResponse): CreditUsageStats | undefined => {
      const stats = normalizeCreditStats(response);

      return isCreditUsageVisible({ pricingModel, isEnforced: stats.isEnforced, creditUsageEnabledForLegacyPlans })
        ? stats
        : undefined;
    },
    [creditUsageEnabledForLegacyPlans, pricingModel],
  );

  return useQuery({
    queryKey: getCreditStatsKey({ workspaceId }),
    queryFn:
      workspaceId && isCreditUsageEnabled
        ? async (): Promise<CreditStatsResponse> => callAirApi(() => Workspaces.Credits.getStats({ workspaceId }))
        : skipToken,
    select: selectVisibleStats,
    /**
     * We want to prevent the somewhat expensive stats endpoint from being called too often, so
     * setting the stale time + refetch interval to one hour. In practice, this will mean that
     * storage/enriched storage usage stats will only be updated once per hour, which should be
     * fine seeing as it is metered in GB units. Generative AI usage will trigger `System.Workspace.Credit.consumed`
     * WebSocket events, and the `useHandleWorkspaceCreditsConsumed` hook will invalidate this query to get
     * the latest stats.
     */
    staleTime: ONE_HOUR,
    refetchInterval: ONE_HOUR,
    refetchIntervalInBackground: false,
  });
};
